"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Service = {
  numeral: string;
  title: string;
  description1: string;
  description2: string;
  duration: string;
  capacity: string;
  pricing: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
const services: Service[] = [{
  numeral: "I",
  title: "Individual Tax Compliance",
  description1: "We provide meticulous tax preparation for individuals, ensuring every deduction is captured and every regulatory requirement is met with precision. Our approach transforms complex filing into a clear, stress-free process.",
  description2: "From income reporting to credit optimization, we guide you through the nuances of personal taxation, ensuring your financial records are accurate and your compliance is beyond reproach.",
  duration: "Annual engagement",
  capacity: "Individual clients",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_compliance.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_compliance.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/individual_tax_compliance.png-wvc-sizes",
  alt: "Professional tax advisor reviewing personal financial documents"
}, {
  numeral: "II",
  title: "Strategic Business Planning",
  description1: "For growing enterprises, we offer strategic tax planning that aligns with your long-term financial goals. We analyze your business structure to identify opportunities for tax efficiency and risk mitigation.",
  description2: "Our team works closely with your leadership to forecast tax liabilities, optimize cash flow, and ensure your business remains compliant while scaling effectively in a competitive market.",
  duration: "Quarterly advisory",
  capacity: "Small to mid-sized firms",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_tax_planning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_tax_planning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/business_tax_planning.png-wvc-sizes",
  alt: "Business strategy meeting with financial charts and tax planning documents"
}, {
  numeral: "III",
  title: "Proactive Risk Management",
  description1: "Tax regulations are constantly evolving. We provide proactive monitoring to protect your assets and ensure your financial strategy remains resilient against regulatory shifts and audit risks.",
  description2: "By maintaining strict ethical standards and deep regulatory knowledge, we help you navigate potential pitfalls, providing peace of mind through rigorous documentation and transparent reporting.",
  duration: "Year-round support",
  capacity: "All client types",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/risk_management_audit.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/risk_management_audit.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/risk_management_audit.png-wvc-sizes",
  alt: "Tax professional analyzing compliance data on a digital dashboard"
}, {
  numeral: "IV",
  title: "Estate & Wealth Advisory",
  description1: "Preserving your legacy requires careful planning. We assist in structuring your estate and wealth management strategies to minimize tax impact and ensure your assets are distributed according to your wishes.",
  description2: "Each plan is tailored to your unique family situation, combining technical tax expertise with a deep understanding of your long-term financial stability and personal objectives.",
  duration: "Bespoke planning",
  capacity: "Private wealth clients",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/estate_wealth_planning.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/estate_wealth_planning.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/estate_wealth_planning.png-wvc-sizes",
  alt: "Consultation session for estate planning and wealth preservation"
}, {
  numeral: "V",
  title: "Tax Resolution Services",
  description1: "If you face inquiries from tax authorities, our team provides expert representation. We handle communications and negotiations, ensuring your rights are protected and issues are resolved efficiently.",
  description2: "We bring clarity to complex disputes, using our deep regulatory knowledge to advocate for your interests and restore your financial standing with the authorities.",
  duration: "As required",
  capacity: "Urgent assistance",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_resolution_support.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_resolution_support.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/tax_resolution_support.png-wvc-sizes",
  alt: "Professional tax advisor providing resolution support in a formal office"
}, {
  numeral: "VI",
  title: "Corporate Tax Compliance",
  description1: "We manage the full spectrum of corporate tax obligations, from filing to reporting. Our systematic approach ensures that your business meets all legal requirements while optimizing your tax position.",
  description2: "Ideal for established companies requiring reliable, year-round advisory and proactive management of their corporate tax footprint to support long-term stability.",
  duration: "Annual/Quarterly",
  capacity: "Corporate entities",
  pricing: "By consultation",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1718253/img/corporate_tax_filing.png-wvc-sizes",
  alt: "Corporate tax filing documents organized on a desk"
}];
function ServiceRow({
  service,
  i
}: {
  service: Service;
  i: number;
}) {
  const imageReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const contentReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const isReversed = i % 2 === 1;
  return <div data-index={i} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
      <div ref={imageReveal.ref} className={`lg:col-span-6 ${isReversed ? "lg:order-2" : "lg:order-1"}`}>
        <div className="relative overflow-hidden border border-border">
          <div className="aspect-[4/5] overflow-hidden">
            <img src={service.image} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-all duration-[1400ms] ease-out ${imageReveal.isVisible ? "scale-100 opacity-100" : "scale-110 opacity-0"}`} />
          </div>
          <div className={`absolute inset-0 bg-background transition-transform duration-[1200ms] ease-[cubic-bezier(0.77,0,0.18,1)] ${imageReveal.isVisible ? "translate-x-full" : "translate-x-0"}`} aria-hidden="true" />
        </div>
      </div>

      <div ref={contentReveal.ref} className={`lg:col-span-6 ${isReversed ? "lg:order-1" : "lg:order-2"} transition-all duration-1000 ease-out ${contentReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className={`${isReversed ? "lg:pr-8" : "lg:pl-8"}`}>
          <div className="font-serif italic text-7xl md:text-8xl text-foreground/10 leading-none mb-6" aria-hidden="true">
            {service.numeral}
          </div>
          <h3 className="font-default text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            {service.title}
          </h3>
          <div className="space-y-4 mb-8">
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
              {service.description1}
            </p>
            <p className="font-default text-base md:text-lg text-muted-foreground leading-relaxed">
              {service.description2}
            </p>
          </div>

          <dl className="mb-10">
            <div className="flex justify-between items-baseline py-4 border-t border-border">
              <dt className="font-mono text-xs uppercase tracking-[0.2em] text-secondary">
                Duration
              </dt>
              <dd className="font-default text-sm md:text-base text-foreground text-right">
                {service.duration}
              </dd>
            </div>
            <div className="flex justify-between items-baseline py-4 border-t border-border">
              <dt className="font-mono text-xs uppercase tracking-[0.2em] text-secondary">
                Capacity
              </dt>
              <dd className="font-default text-sm md:text-base text-foreground text-right">
                {service.capacity}
              </dd>
            </div>
            <div className="flex justify-between items-baseline py-4 border-t border-b border-border">
              <dt className="font-mono text-xs uppercase tracking-[0.2em] text-secondary">
                Pricing
              </dt>
              <dd className="font-default text-sm md:text-base text-foreground text-right">
                {service.pricing}
              </dd>
            </div>
          </dl>

          <Button asChild variant="outline" size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 font-mono text-xs uppercase tracking-[0.25em] h-12 px-8 border-none">
            <Link to="#">
              Inquire <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function TaxExpertise() {
  const introReveal = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4412" id="comprehensive-tax-expertise" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-10 overflow-hidden">
      <div ref={introReveal.ref} className={`max-w-5xl mx-auto text-center transition-all duration-1000 ease-out ${introReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <p className="font-mono text-xs md:text-sm uppercase tracking-[0.35em] text-secondary mb-10">
          Professional Tax Services
        </p>
        <h2 className="font-default text-5xl md:text-7xl lg:text-8xl leading-[1.05] text-foreground mb-10">
          Financial clarity,
          <br />
          <span className="italic text-secondary">expertly delivered.</span>
        </h2>
        <p className="font-default italic text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed mb-16">
          Lovejoy Company provides tailored tax solutions for individuals and businesses, ensuring compliance, strategic planning, and long-term financial stability.
        </p>
        <div className="mx-auto h-px w-24 bg-accent" aria-hidden="true" />
      </div>

      <div className="max-w-7xl mx-auto mt-32 md:mt-40 space-y-32 md:space-y-48">
        {services.map((service, i) => <ServiceRow key={service.numeral} service={service} i={i} />)}
      </div>
    </section>;
}